import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Button,
  IconButton,
  Autocomplete,
  Divider,
  Chip,
  alpha,
  InputAdornment,
  Tooltip,
  Collapse,
  MenuItem,
  Switch,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { FormikProvider, useFormik } from "formik";
import { useNotifier } from "../../../Core/Notifier";
import {
  useCreateStudentMutation,
  useGetallEnumSearchQuery,
  useGetallGradeQuery,
  useGetallLocationQuery,
  useLookupStudentperentQuery,
  useEnumFindOrCreateMutation,
} from "../../../Api/attoDeskApi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { CameraModal, CropModal } from "../../../Components/ImageCapture";
import NewDepartement from "../StudentPerent/index";
import {
  CustomerOption,
  CustomerOptionString,
  IGrade,
  IEnumType,
  IStudentCreate,
  IStudentPerent,
  IStudentLocation,
} from "../../../Api/Interface/api.interface";
import { useNavigate } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import JoinedMonthPicker from "../../../Components/DatePicker/JoinedMonthPicker";
import {
  formatJoinedMonthForApi,
  isJoinedMonthInFuture,
} from "../../../Components/DatePicker/joinedMonth";
import {
  Gender,
  RelationshipType,
  heardFrom,
} from "../../../Core/Enum/enum";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { capitalizeFirstLetter } from "../../../Components/Validation/capitalizeFirstLetter";
import {
  validateEmail,
  validatePhoneNumber,
  formatPhoneNumberForDisplay,
} from "../../../Components/Validation/validationUtils";
import BackDrop from "../../../Components/Loader/BackDrop";
import AddressAutocomplete, { ParsedAddress } from "../../../Components/AddressAutocomplete";
import RadioCardGroup, { RadioCardOption } from "../../../Components/RadioCardGroup";
import { STUDENT_ENROLLMENT_RADIO_OPTIONS } from "../../../Core/constants/studentEnrollmentTypes";
import StudentBillingTypeSection, {
  BillingTypeV2,
  StudentBillingValues,
  StudentBillingErrors,
} from "../Component/StudentBillingTypeSection";
import { validateMonthlyAmount } from "../Component/studentBillingValidation";

// Icons
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import SchoolIcon from "@mui/icons-material/School";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const cap = (s: string | null | undefined) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const toAllCaps = (text: string | null | undefined): string =>
  text ? text.toUpperCase() : "";

// Enrollment classification options — sourced from the central
// STUDENT_ENROLLMENT_TYPES constant so Create / Edit / Profile always
// stay in sync. Adding a fifth type is one edit in that file plus a
// matching backend enum entry.
const PROGRAM_TYPE_OPTIONS: RadioCardOption[] = STUDENT_ENROLLMENT_RADIO_OPTIONS;

// ────────────────────────────────────────
// Section Card Component
// ────────────────────────────────────────
const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  accentColor?: string;
  step?: number;
}> = ({ icon, title, subtitle, children, accentColor, step }) => {
  const theme = useTheme();
  const color = accentColor || theme.palette.primary.main;
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        border: "1px solid rgba(0,0,0,0.08)",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          borderLeft: `4px solid ${color}`,
        }}
      >
        {step && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              bgcolor: alpha(color, 0.1),
              color: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {step}
          </Box>
        )}
        <Box
          sx={{
            color: color,
            display: "flex",
            alignItems: "center",
            "& svg": { fontSize: 22 },
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "text.primary", lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.1 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {/* Section Body */}
      <Box sx={{ p: 3 }}>{children}</Box>
    </Box>
  );
};

// ────────────────────────────────────────
// Field Label Component
// ────────────────────────────────────────
const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({ label, required }) => (
  <Typography
    sx={{
      fontSize: 13,
      fontWeight: 500,
      color: "text.secondary",
      mb: 0.75,
      letterSpacing: "-0.01em",
    }}
  >
    {label}
    {required && (
      <Typography component="span" sx={{ color: "error.main", ml: 0.25 }}>
        *
      </Typography>
    )}
  </Typography>
);

// ────────────────────────────────────────
// Main Component
// ────────────────────────────────────────
const Index = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  // Image
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Parent search
  const [parentSearchQuery, setParentSearchQuery] = useState("");
  // 450ms debounce keeps us well under the perceived-typing threshold while
  // avoiding a request per keystroke on the lookup endpoint.
  const debouncedParentSearch = useDebounce(parentSearchQuery, 450);
  const parentSearchReady = debouncedParentSearch.trim().length >= 3;
  const [openCreateParent, setOpenCreateParent] = useState(false);

  // Multi-parent support
  interface ParentEntry {
    parent_id: number;
    parentInfo: IStudentPerent;
    relationship_type: string;
    isPrimary: boolean;
  }
  const [parentEntries, setParentEntries] = useState<ParentEntry[]>([]);

  // Additional sections
  const [showHeardFrom, setShowHeardFrom] = useState(false);

  // API hooks
  const [createStudent, { isLoading }] = useCreateStudentMutation();
  const [enumFindOrCreate] = useEnumFindOrCreateMutation();

  const { data: gradeData, isLoading: gradeIsLoading } = useGetallGradeQuery({ searchText: "" });
  const { data: provinceData, isLoading: provinceIsLoading } = useGetallEnumSearchQuery({ searchText: "", enum_type: "province" });
  const { data: countryData, isLoading: countryIsLoading } = useGetallEnumSearchQuery({ searchText: "", enum_type: "country" });
  const { data: cityData, isLoading: cityIsLoading } = useGetallEnumSearchQuery({ searchText: "", enum_type: "city" });
  const { data: locationData, isLoading: locationIsLoading } = useGetallLocationQuery({ searchText: "" });
  const {
    data: studentPerentData,
    // isFetching (not isLoading) is true on EVERY search, including refetches;
    // isLoading is only true on the first-ever load, which would leave the
    // spinner dark and stale results on screen for subsequent searches.
    isFetching: parentIsFetching,
    isError: parentIsError,
  } = useLookupStudentperentQuery(
    { q: debouncedParentSearch.trim(), limit: 10 },
    // skip = never fires until the user has typed 3+ chars, so the endpoint
    // is not called on focus. RTK Query dedupes identical args and
    // supersedes older requests when args change, giving us
    // dedupe + cancel-previous for free.
    { skip: !parentSearchReady }
  );

  // Memo lists
  const gradeList = useMemo(() => (gradeData?.data ?? []) as IGrade[], [gradeData]);
  const parentList = useMemo(
    () => (parentSearchReady ? ((studentPerentData?.data ?? []) as IStudentPerent[]) : []),
    [studentPerentData, parentSearchReady]
  );
  const provinceList = useMemo(() => (provinceData?.data ?? []) as IEnumType[], [provinceData]);
  const countryList = useMemo(() => (countryData?.data ?? []) as IEnumType[], [countryData]);
  const cityList = useMemo(() => (cityData?.data ?? []) as IEnumType[], [cityData]);
  const locationList = useMemo(() => (locationData?.data ?? []) as IStudentLocation[], [locationData]);

  // Autocomplete options
  const gradeOptions: CustomerOption[] = useMemo(
    () => gradeList.map((g) => ({ label: g.grade, value: g.id })),
    [gradeList]
  );
  const parentOptions = useMemo(
    () =>
      parentList.map((p) => ({
        label: `${cap(p.first_name)} ${cap(p.last_name)}`.trim(),
        sublabel: [p.contact_number ? formatPhoneNumberForDisplay(p.contact_number) : "", p.email].filter(Boolean).join(" | "),
        value: p.id,
        raw: p,
      })),
    [parentList]
  );
  const provinceOptions: CustomerOption[] = useMemo(() => provinceList.map((e) => ({ label: e.enum_value, value: e.id })), [provinceList]);
  const countryOptions: CustomerOption[] = useMemo(() => countryList.map((e) => ({ label: e.enum_value, value: e.id })), [countryList]);
  const cityOptions: CustomerOption[] = useMemo(() => cityList.map((e) => ({ label: e.enum_value, value: e.id })), [cityList]);
  // Exclude inactive/deleted locations — backend validation rejects them too,
  // but hiding them from the dropdown avoids a confusing "invalid selection"
  // error after the fact.
  const locationOptions: CustomerOption[] = useMemo(
    () =>
      locationList
        .filter((l) => l.isActive !== false && l.isActive !== 0 && !l.isDelete)
        .map((l) => ({ label: l.location_name || l.location_address, value: l.id })),
    [locationList]
  );
  const genderOptions: CustomerOptionString[] = Object.values(Gender).map((g) => ({ label: g, value: g }));

  // ── Grade auto-suggest from DOB ──
  const [gradeAutoSuggested, setGradeAutoSuggested] = useState(false);

  /**
   * Calculate expected school grade from date of birth.
   * Uses Ontario cutoff: child must turn the age by Dec 31 of the school year.
   * School year starts in September.
   *
   * Age by Dec 31 → Grade:
   *   3 or under → Pre-K / Nursery
   *   4          → JK (Junior Kindergarten)
   *   5          → SK (Senior Kindergarten)
   *   6          → Grade 1
   *   7          → Grade 2
   *   ...
   *   17         → Grade 12
   *   18+        → Post-secondary / Adult
   *
   * Returns the expected grade label and approximate order_number.
   */
  const getExpectedGradeFromDOB = (dob: any) => {
    if (!dob) return null;

    const birthDate = dob.toDate ? dob.toDate() : new Date(dob);
    if (isNaN(birthDate.getTime())) return null;

    const now = new Date();
    // Current school year: if we're in Jan-Aug, school year started last Sept
    // If Sep-Dec, school year started this Sept
    const currentMonth = now.getMonth(); // 0-indexed (0=Jan, 8=Sep)
    const schoolYearStart = currentMonth >= 8 ? now.getFullYear() : now.getFullYear() - 1;

    // Age the child will be by Dec 31 of the current school year
    const dec31 = new Date(schoolYearStart, 11, 31);
    let ageByDec31 = dec31.getFullYear() - birthDate.getFullYear();
    // Adjust if birthday hasn't occurred by Dec 31
    const birthdayThisYear = new Date(dec31.getFullYear(), birthDate.getMonth(), birthDate.getDate());
    if (birthdayThisYear > dec31) ageByDec31--;

    if (ageByDec31 < 3) return null; // too young

    // Map age to grade level
    // age 4 → JK (order ~0), age 5 → SK (order ~0), age 6 → Grade 1 (order 1), etc.
    const gradeLevel = ageByDec31 - 5; // age 6 = grade 1, age 7 = grade 2, etc.

    // Build search terms to match against grade names
    const searchTerms: string[] = [];

    if (ageByDec31 <= 3) {
      searchTerms.push("pre-k", "prek", "nursery", "preschool", "toddler");
    } else if (ageByDec31 === 4) {
      searchTerms.push("jk", "junior kindergarten", "junior k", "pre-k", "kindergarten");
    } else if (ageByDec31 === 5) {
      searchTerms.push("sk", "senior kindergarten", "senior k", "kindergarten");
    } else if (gradeLevel >= 1 && gradeLevel <= 12) {
      searchTerms.push(
        `grade ${gradeLevel}`,
        `grade${gradeLevel}`,
        `gr ${gradeLevel}`,
        `gr${gradeLevel}`,
        `g${gradeLevel}`,
        `${gradeLevel}`,
      );
      // Also try ordinal: "1st", "2nd", "3rd", etc.
      const ordinal = gradeLevel === 1 ? "1st" : gradeLevel === 2 ? "2nd" : gradeLevel === 3 ? "3rd" : `${gradeLevel}th`;
      searchTerms.push(ordinal);
    } else {
      searchTerms.push("adult", "post-secondary", "continuing", "12");
    }

    return { gradeLevel, ageByDec31, searchTerms };
  };

  const autoSelectGrade = (dob: any) => {
    const result = getExpectedGradeFromDOB(dob);
    if (!result || gradeOptions.length === 0) return;

    const { searchTerms, gradeLevel } = result;

    // Try to find matching grade from the options
    let matched: CustomerOption | null = null;

    for (const term of searchTerms) {
      const lower = term.toLowerCase();
      // Exact match on grade name
      const exact = gradeOptions.find((g) => g.label.toLowerCase().trim() === lower);
      if (exact) { matched = exact; break; }
      // Contains match
      const contains = gradeOptions.find((g) => g.label.toLowerCase().includes(lower));
      if (contains) { matched = contains; break; }
    }

    // Fallback: match by order_number if the grade list is ordered by level
    if (!matched && gradeLevel >= 1) {
      const byOrder = gradeList.find((g) => g.order_number === gradeLevel);
      if (byOrder) {
        matched = gradeOptions.find((o) => o.value === byOrder.id) || null;
      }
    }

    if (matched) {
      formik.setFieldValue("grade_id", matched.value);
      setGradeAutoSuggested(true);
    }
  };

  // Formik
  const formik = useFormik<IStudentCreate>({
    initialValues: {
      first_name: "",
      middle_name: "",
      last_name: "",
      gender: "",
      date_of_birth: null,
      joined_month: null,
      study_mode: "",
      student_country_id: "",
      location_id: "",
      image: "",
      notes: "",
      heard_from: "",
      unit_number: "",
      street: "",
      city_id: "",
      province_id: "",
      postal_code: "",
      country_id: "",
      parent_id: "",
      grade_id: "",
      program_type: "",
      relationship_id: "",
      relationship_type: "",
      address_id: "",
      // Tax Applicable — default OFF (matches DB default). Admin
      // flips it on when the student should be charged tax.
      tax_applicable: false,
      // Billing V2 — student picks Monthly vs Class-Wise at create time.
      // Not defaulted silently: the admin must choose. Effective date
      // defaults to today so the arrangement takes effect immediately.
      // monthly_amount is stored as a raw string ("" | "5" | "300.00")
      // and Number()-normalised on submit — never as a formatted "$5".
      billing_type_v2: "" as BillingTypeV2 | "",
      monthly_amount: "" as string,
      billing_effective_from: dayjs().format("YYYY-MM-DD"),
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (!values.first_name?.trim()) errors.first_name = "First name is required";
      if (!values.last_name?.trim()) errors.last_name = "Last name is required";
      if (!values.grade_id) errors.grade_id = "Grade is required";
      if (!values.program_type) errors.program_type = "Enrollment type is required";
      if (values.date_of_birth) {
        const selectedDate = values.date_of_birth.toDate();
        if (selectedDate >= new Date()) errors.date_of_birth = "Must be in the past";
      }
      if (isJoinedMonthInFuture(values.joined_month)) {
        errors.joined_month = "Joined month cannot be in the future.";
      }
      if (!values.location_id) {
        errors.location_id = "Please select a default location.";
      }
      if (!values.student_country_id) {
        errors.student_country_id = "Country of origin is required.";
      }
      // Billing V2 validation. The admin must pick a billing type; Monthly
      // additionally needs an amount > 0. Effective date is date-picked so
      // it can't be arbitrary text but is required on both types.
      //
      // monthly_amount is stored as a raw string (see StudentBillingTypeSection
      // onChange). Convert to Number here — never validate the display value.
      if (!values.billing_type_v2) {
        errors.billing_type_v2 = "Billing type is required.";
      } else {
        if (!values.billing_effective_from) {
          errors.billing_effective_from = "Billing effective date is required.";
        }
        const amountErr = validateMonthlyAmount(
          values.billing_type_v2,
          values.monthly_amount
        );
        if (amountErr) errors.monthly_amount = amountErr;
      }
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        let image: string | undefined;
        if (selectedImage) {
          setIsImageUploading(true);
          image = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }

        const payload = {
          first_name: capitalizeFirstLetter(values.first_name),
          middle_name: capitalizeFirstLetter(values.middle_name),
          last_name: capitalizeFirstLetter(values.last_name),
          gender: values.gender,
          date_of_birth: values.date_of_birth ? values.date_of_birth.format("YYYY-MM-DD") : null,
          joined_month: formatJoinedMonthForApi(values.joined_month),
          study_mode: values.study_mode,
          student_country_id: values.student_country_id,
          location_id: values.location_id,
          image,
          notes: values.notes,
          unit_number: values.unit_number,
          street: values.street,
          city_id: values.city_id,
          province_id: values.province_id,
          postal_code: toAllCaps(values.postal_code),
          country_id: values.country_id,
          heard_from: values.heard_from,
          parent_id: values.parent_id,
          grade_id: values.grade_id,
          program_type: values.program_type || null,
          relationship_id: values.relationship_id,
          relationship_type: values.relationship_type,
          address_id: values.address_id,
          tax_applicable: !!values.tax_applicable,
          // Billing V2 — routed through BillingHistoryWriter server-side.
          // monthly_amount is normalised to a Number at payload time (never
          // sent as a formatted string like "$5"); null on Class-Wise so
          // the server never stores a stale monthly figure.
          billing_type_v2: values.billing_type_v2 || null,
          monthly_amount:
            values.billing_type_v2 === "monthly"
              ? Number(values.monthly_amount)
              : null,
          billing_effective_from: values.billing_effective_from,
          parents: parentEntries.map((e) => ({
            parent_id: e.parent_id,
            relationship_type: e.relationship_type,
            isPrimary: e.isPrimary,
          })),
        };

        const response = await createStudent(payload).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message || "Student created successfully");
          navigate(`/student-history/${(response.data as any)?.id || ""}`);
          resetForm();
          setSelectedImage(undefined);
          setParentEntries([]);
        }
      } catch (error: any) {
        console.error("Student create failed:", error);
        const msg =
          error?.data?.message ||
          error?.message ||
          error?.error ||
          (typeof error === "string" ? error : "Something went wrong");
        showErrorMessage(msg);
      }
    },
  });

  const formValid = useMemo(() => {
    // monthly_amount lives as a raw string in Formik — Number() converts
    // "5" / "5.50" / "300.00" to a finite positive number; empty string,
    // "abc", or "0" fail.
    const amount = Number(formik.values.monthly_amount);
    const monthlyOk =
      formik.values.billing_type_v2 !== "monthly"
      || (Number.isFinite(amount) && amount > 0);
    const billingOk =
      !!formik.values.billing_type_v2 &&
      !!formik.values.billing_effective_from &&
      monthlyOk;
    return !!(
      formik.values.first_name?.trim() &&
      formik.values.last_name?.trim() &&
      formik.values.grade_id &&
      formik.values.program_type &&
      billingOk
    );
  }, [
    formik.values.first_name,
    formik.values.last_name,
    formik.values.grade_id,
    formik.values.program_type,
    formik.values.billing_type_v2,
    formik.values.monthly_amount,
    formik.values.billing_effective_from,
  ]);

  // Image handling
  const resizeImage = (file: File, maxW: number, maxH: number): Promise<File> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          if (w > h) { if (w > maxW) { h *= maxW / w; w = maxW; } }
          else { if (h > maxH) { w *= maxH / h; h = maxH; } }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d")?.drawImage(img, 0, 0, w, h);
          canvas.toBlob((blob) => {
            blob ? resolve(new File([blob], file.name, { type: file.type })) : reject(new Error("Canvas empty"));
          }, file.type);
        };
        img.onerror = reject;
      };
    });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { showErrorMessage("File must be an image"); return; }
    if (file.size > 5 * 1024 * 1024) { showErrorMessage("File must be under 5 MB"); return; }
    // Open crop modal with the uploaded image
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCameraCapture = (dataUrl: string) => {
    setCropImageSrc(dataUrl);
    setCropOpen(true);
  };

  const handleCropComplete = (croppedFile: File) => {
    setSelectedImage(croppedFile);
  };

  // ── Address auto-fill handler ──
  // Finds matching enum IDs, or auto-creates missing city/province/country entries
  const handleAddressAutoFill = useCallback(
    async (addr: ParsedAddress) => {
      // Street
      if (addr.street) formik.setFieldValue("street", addr.street);

      // Postal code
      if (addr.postalCode) formik.setFieldValue("postal_code", addr.postalCode.toUpperCase());

      // Helper: find enum match from options list
      const findMatch = (options: CustomerOption[], candidates: string[]) => {
        for (const candidate of candidates) {
          if (!candidate) continue;
          const lower = candidate.toLowerCase().trim();
          for (const opt of options) {
            const optLower = opt.label.toLowerCase().trim();
            if (optLower === lower || optLower.includes(lower) || lower.includes(optLower)) {
              return opt;
            }
          }
        }
        return null;
      };

      // ── City: try matching first, auto-create if not found ──
      const cityCandidates = [addr.suburb, addr.city].filter(Boolean);
      const cityMatch = findMatch(cityOptions, cityCandidates);
      if (cityMatch) {
        formik.setFieldValue("city_id", String(cityMatch.value));
      } else {
        // Auto-create the city — use suburb first (more specific), then city
        const cityName = addr.suburb || addr.city;
        if (cityName) {
          try {
            const result = await enumFindOrCreate({
              enum_type: "city",
              enum_value: cityName,
            }).unwrap();
            if (result.status && result.data) {
              formik.setFieldValue("city_id", String((result.data as any).id));
            }
          } catch {
            // Silently fail — user can still pick manually
          }
        }
      }

      // ── Province: try matching first, auto-create if not found ──
      if (addr.province) {
        const provMatch = findMatch(provinceOptions, [addr.province]);
        if (provMatch) {
          formik.setFieldValue("province_id", String(provMatch.value));
        } else {
          try {
            const result = await enumFindOrCreate({
              enum_type: "province",
              enum_value: addr.province,
            }).unwrap();
            if (result.status && result.data) {
              formik.setFieldValue("province_id", String((result.data as any).id));
            }
          } catch {
            // Silently fail
          }
        }
      }

      // ── Country: try matching first, auto-create if not found ──
      if (addr.country) {
        const countryMatch = findMatch(countryOptions, [addr.country]);
        if (countryMatch) {
          formik.setFieldValue("country_id", String(countryMatch.value));
        } else {
          try {
            const result = await enumFindOrCreate({
              enum_type: "country",
              enum_value: addr.country,
            }).unwrap();
            if (result.status && result.data) {
              formik.setFieldValue("country_id", String((result.data as any).id));
            }
          } catch {
            // Silently fail
          }
        }
      }
    },
    [cityOptions, provinceOptions, countryOptions, formik, enumFindOrCreate]
  );

  // ── City free-text handler ──
  // When the user types a city name that isn't in the dropdown, find-or-create it as an enum
  const handleCityFreeText = async (cityName: string) => {
    if (!cityName.trim()) return;
    try {
      const result = await enumFindOrCreate({
        enum_type: "city",
        enum_value: cityName.trim(),
      }).unwrap();
      if (result.status && result.data) {
        formik.setFieldValue("city_id", String((result.data as any).id));
      }
    } catch {
      // Silently fail — the value stays typed but without an ID
    }
  };

  // Parent selection handler — adds to the list instead of replacing
  const handleParentSelect = (parent: any) => {
    if (!parent?.raw) return;
    // Don't add duplicate
    if (parentEntries.some((e) => e.parent_id === parent.raw.id)) return;

    const isFirst = parentEntries.length === 0;
    const newEntry: ParentEntry = {
      parent_id: parent.raw.id,
      parentInfo: parent.raw,
      relationship_type: formik.values.relationship_type || "Parent",
      isPrimary: isFirst, // first parent added is primary by default
    };
    const updated = [...parentEntries, newEntry];
    setParentEntries(updated);
    syncParentsToFormik(updated);
    setParentSearchQuery("");
  };

  const handleRemoveParent = (parentId: number) => {
    let updated = parentEntries.filter((e) => e.parent_id !== parentId);
    // If we removed the primary, make the first remaining one primary
    if (updated.length > 0 && !updated.some((e) => e.isPrimary)) {
      updated[0].isPrimary = true;
    }
    setParentEntries(updated);
    syncParentsToFormik(updated);
  };

  const handleSetPrimary = (parentId: number) => {
    const updated = parentEntries.map((e) => ({
      ...e,
      isPrimary: e.parent_id === parentId,
    }));
    setParentEntries(updated);
    syncParentsToFormik(updated);
  };

  const handleChangeRelationship = (parentId: number, relType: string) => {
    const updated = parentEntries.map((e) =>
      e.parent_id === parentId ? { ...e, relationship_type: relType } : e
    );
    setParentEntries(updated);
  };

  // Sync multi-parent data into formik for submission
  const syncParentsToFormik = (entries: ParentEntry[]) => {
    const primary = entries.find((e) => e.isPrimary) || entries[0];
    if (primary) {
      formik.setFieldValue("parent_id", primary.parent_id);
      formik.setFieldValue("relationship_type", primary.relationship_type);
    } else {
      formik.setFieldValue("parent_id", "");
      formik.setFieldValue("relationship_type", "");
    }
  };

  // Deliberately no top-level loader here. The Create Student form
  // shell + Basic Information section renders immediately; each lookup
  // (grade, province, country, city, location) drives its own tiny
  // per-field `loading` prop below so a slow lookup never blocks
  // typing First Name / Last Name / DOB. Parent search is separately
  // lazy (skipped until 3+ chars) and never fires on mount.

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Page Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={() => navigate("/student/list")}
            sx={{ border: "1px solid rgba(0,0,0,0.1)", borderRadius: "8px", p: 0.75 }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em" }}>
              Add New Student
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Fill in the details below to register a new student
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={() => { formik.resetForm(); setSelectedImage(undefined); setParentEntries([]); }}
            sx={{ color: "text.secondary", borderColor: "rgba(0,0,0,0.12)" }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => formik.handleSubmit()}
            disabled={!formValid || isLoading}
          >
            Save Student
          </Button>
        </Box>
      </Box>

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* ─────────── SECTION 1: Basic Info ─────────── */}
            <SectionCard
              step={1}
              icon={<PersonOutlineIcon />}
              title="Basic Information"
              subtitle="Student's personal details and photo"
              accentColor={theme.palette.primary.main}
            >
              <Grid container spacing={3}>
                {/* Photo Capture / Upload */}
                <Grid item lg={3} md={4} sm={12} xs={12}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    {/* Preview */}
                    <Box
                      sx={{
                        position: "relative",
                        width: 160,
                        height: 160,
                        borderRadius: "50%",
                        border: selectedImage
                          ? `3px solid ${theme.palette.primary.main}`
                          : "2px dashed rgba(0,0,0,0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        bgcolor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      {selectedImage ? (
                        <>
                          <img
                            src={URL.createObjectURL(selectedImage)}
                            alt="Preview"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                          {/* Re-edit overlay */}
                          <Box
                            onClick={() => {
                              // Re-open crop with current image
                              const url = URL.createObjectURL(selectedImage);
                              setCropImageSrc(url);
                              setCropOpen(true);
                            }}
                            sx={{
                              position: "absolute",
                              inset: 0,
                              bgcolor: "rgba(0,0,0,0.4)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: 0,
                              transition: "opacity 0.2s",
                              cursor: "pointer",
                              "&:hover": { opacity: 1 },
                            }}
                          >
                            <EditOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
                          </Box>
                        </>
                      ) : (
                        <Box sx={{ textAlign: "center" }}>
                          <CloudUploadIcon sx={{ fontSize: 32, color: "rgba(0,0,0,0.2)", mb: 0.25 }} />
                          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>No Photo</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CameraAltIcon sx={{ fontSize: "16px !important" }} />}
                        onClick={() => setCameraOpen(true)}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          fontSize: 12,
                          fontWeight: 500,
                          px: 1.5,
                          py: 0.5,
                          borderColor: "rgba(0,0,0,0.15)",
                          color: "text.secondary",
                        }}
                      >
                        Camera
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon sx={{ fontSize: "16px !important" }} />}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          fontSize: 12,
                          fontWeight: 500,
                          px: 1.5,
                          py: 0.5,
                          borderColor: "rgba(0,0,0,0.15)",
                          color: "text.secondary",
                        }}
                      >
                        Upload
                      </Button>
                    </Box>
                    {selectedImage && (
                      <Button
                        size="small"
                        onClick={() => setSelectedImage(undefined)}
                        sx={{ fontSize: 11, textTransform: "none", color: "text.disabled" }}
                      >
                        Remove
                      </Button>
                    )}
                    <input
                      ref={fileInputRef}
                      accept="image/*"
                      type="file"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                  </Box>
                </Grid>

                {/* Name Fields */}
                <Grid item lg={9} md={8} sm={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="First Name" required />
                      <TextField
                        placeholder="Enter first name"
                        size="small"
                        fullWidth
                        {...formik.getFieldProps("first_name")}
                        error={!!(formik.touched.first_name && formik.errors.first_name)}
                        helperText={formik.touched.first_name && formik.errors.first_name}
                      />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Middle Name" />
                      <TextField
                        placeholder="Enter middle name"
                        size="small"
                        fullWidth
                        {...formik.getFieldProps("middle_name")}
                      />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Last Name" required />
                      <TextField
                        placeholder="Enter last name"
                        size="small"
                        fullWidth
                        {...formik.getFieldProps("last_name")}
                        error={!!(formik.touched.last_name && formik.errors.last_name)}
                        helperText={formik.touched.last_name && formik.errors.last_name}
                      />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Gender" />
                      <Autocomplete
                        options={genderOptions}
                        getOptionLabel={(o: CustomerOptionString) => o.label}
                        value={genderOptions.find((o) => o.value === formik.values.gender) || null}
                        onChange={(_, v) => formik.setFieldValue("gender", v?.value || "")}
                        renderInput={(params) => <TextField {...params} placeholder="Select gender" size="small" />}
                      />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Date of Birth" />
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={formik.values.date_of_birth}
                          onChange={(d) => {
                            formik.setFieldValue("date_of_birth", d);
                            autoSelectGrade(d);
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              error: !!formik.errors.date_of_birth,
                              helperText: formik.errors.date_of_birth as string,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Grade" required />
                      <Autocomplete
                        options={gradeOptions}
                        loading={gradeIsLoading}
                        getOptionLabel={(o: CustomerOption) => o.label}
                        value={gradeOptions.find((o) => o.value === Number(formik.values.grade_id)) || null}
                        onChange={(_, v) => {
                          formik.setFieldValue("grade_id", v?.value || "");
                          setGradeAutoSuggested(false); // user manually changed it
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select grade"
                            size="small"
                            error={!!(formik.touched.grade_id && formik.errors.grade_id)}
                            helperText={
                              formik.touched.grade_id && formik.errors.grade_id
                                ? formik.errors.grade_id
                                : gradeAutoSuggested
                                ? "Auto-suggested based on date of birth"
                                : undefined
                            }
                            FormHelperTextProps={{
                              sx: gradeAutoSuggested ? { color: "success.main", fontWeight: 500 } : {},
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Enrollment Type — radio cards. See §1.3(c) rationale
                       in the PROGRAM_TYPE_OPTIONS constant above. */}
                    <Grid item xs={12}>
                      <FieldLabel label="Enrollment Type" required />
                      <RadioCardGroup
                        options={PROGRAM_TYPE_OPTIONS}
                        value={formik.values.program_type}
                        onChange={(v) => formik.setFieldValue("program_type", v)}
                        columns={2}
                        error={!!(formik.touched.program_type && formik.errors.program_type)}
                      />
                      {formik.touched.program_type && formik.errors.program_type && (
                        <Typography sx={{ fontSize: 11, color: "error.main", mt: 0.5, ml: 0.25 }}>
                          {formik.errors.program_type as string}
                        </Typography>
                      )}
                    </Grid>

                    {/* Tax Applicable ─────────────────────────────────
                        Canonical positive-phrased toggle (spec: never
                        label as "Tax Exempt" when ON means taxed).
                        Default OFF so new students inherit the historical
                        tax-exempt behaviour until an admin opts them in. */}
                    <Grid item xs={12}>
                      <FieldLabel label="Tax Applicable" />
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: 2,
                        p: 2, borderRadius: "12px",
                        border: `1px solid ${alpha(formik.values.tax_applicable ? "#f59e0b" : "#22c55e", 0.35)}`,
                        bgcolor: alpha(formik.values.tax_applicable ? "#f59e0b" : "#22c55e", 0.06),
                      }}>
                        <Box sx={{
                          width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          bgcolor: alpha(formik.values.tax_applicable ? "#f59e0b" : "#22c55e", 0.15),
                          color: formik.values.tax_applicable ? "#b45309" : "#16a34a",
                        }}>
                          <ReceiptLongOutlinedIcon />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            {formik.values.tax_applicable ? "Taxable" : "Tax Exempt"}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                            {formik.values.tax_applicable
                              ? "Applicable taxes will be added to this student's class fees and invoices."
                              : "No tax will be added to this student's invoices."}
                          </Typography>
                        </Box>
                        <Switch
                          checked={!!formik.values.tax_applicable}
                          onChange={(e) => formik.setFieldValue("tax_applicable", e.target.checked)}
                          inputProps={{ "aria-label": "Tax applicable" }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </SectionCard>

            {/* ─────────── SECTION 2: Parent Info ─────────── */}
            <SectionCard
              step={2}
              icon={<FamilyRestroomIcon />}
              title="Parent / Guardian"
              subtitle="Add one or more parents. Mark one as primary for billing."
              accentColor="#8b5cf6"
            >
              {/* Search + Add row */}
              <Box sx={{ mb: 2.5 }}>
                <FieldLabel label="Search Parent by Name, Phone, or Email" />
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: { xs: "wrap", md: "nowrap" } }}>
                  <Box sx={{ flex: 1, minWidth: 280 }}>
                    <Autocomplete
                      options={parentOptions.filter(
                        (o) => !parentEntries.some((e) => e.parent_id === o.value)
                      )}
                      loading={parentIsFetching}
                      loadingText="Searching…"
                      noOptionsText={
                        !parentSearchReady
                          ? "Type at least 3 characters to search"
                          : parentIsError
                            ? "Unable to search parents. Please try again."
                            : "No matching parent or guardian found"
                      }
                      // The options list is bound to the latest debounced term
                      // (keepUnusedDataFor: 0), so a slower earlier response can
                      // never replace results for a newer search, and old
                      // results clear the moment the search value changes.
                      filterOptions={(x) => x}
                      getOptionLabel={(o: any) => o.label}
                      value={null}
                      onChange={(_, v) => handleParentSelect(v)}
                      onInputChange={(_, val, reason) => {
                        if (reason === "input") setParentSearchQuery(val);
                      }}
                      renderOption={(props, option: any) => (
                        <li {...props} key={option.value}>
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{option.label}</Typography>
                            {option.sublabel && (
                              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                                {option.sublabel}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Type name, phone or email to search..."
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonSearchIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.3)" }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <>
                                {parentIsFetching && <CircularProgress size={18} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: "text.disabled", alignSelf: "center", fontSize: 13 }}>
                    OR
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCreateParent(true)}
                    sx={{
                      borderColor: "#8b5cf6",
                      color: "#8b5cf6",
                      "&:hover": { borderColor: "#7c3aed", bgcolor: "rgba(139,92,246,0.04)" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    New Parent
                  </Button>
                </Box>
              </Box>

              {/* Added Parents List */}
              {parentEntries.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {parentEntries.map((entry) => (
                    <Box
                      key={entry.parent_id}
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        bgcolor: entry.isPrimary ? "rgba(34,197,94,0.04)" : "rgba(0,0,0,0.015)",
                        border: entry.isPrimary
                          ? "1px solid rgba(34,197,94,0.25)"
                          : "1px solid rgba(0,0,0,0.08)",
                        transition: "all 0.15s",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        {/* Primary badge */}
                        {entry.isPrimary ? (
                          <Tooltip title="Primary contact for billing & payments">
                            <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 22, flexShrink: 0 }} />
                          </Tooltip>
                        ) : (
                          <Box sx={{ width: 22, height: 22, flexShrink: 0 }} />
                        )}

                        {/* Parent info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                              {cap(entry.parentInfo.first_name)} {cap(entry.parentInfo.last_name)}
                            </Typography>
                            {entry.isPrimary && (
                              <Chip
                                label="Primary - Billing"
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  bgcolor: "rgba(34,197,94,0.12)",
                                  color: "#15803d",
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 2, mt: 0.25, flexWrap: "wrap" }}>
                            {entry.parentInfo.contact_number && (
                              <Typography sx={{ fontSize: 12, color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14 }} />
                                {formatPhoneNumberForDisplay(entry.parentInfo.contact_number)}
                              </Typography>
                            )}
                            {entry.parentInfo.email && (
                              <Typography sx={{ fontSize: 12, color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14 }} />
                                {entry.parentInfo.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Relationship type selector */}
                        <TextField
                          select
                          size="small"
                          value={entry.relationship_type}
                          onChange={(e) => handleChangeRelationship(entry.parent_id, e.target.value)}
                          sx={{ minWidth: 120, "& .MuiInputBase-input": { fontSize: 12 } }}
                        >
                          {Object.values(RelationshipType).map((t) => (
                            <MenuItem key={t} value={t} sx={{ fontSize: 12 }}>{t}</MenuItem>
                          ))}
                        </TextField>

                        {/* Actions */}
                        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                          {!entry.isPrimary && (
                            <Tooltip title="Set as primary for billing">
                              <Button
                                size="small"
                                onClick={() => handleSetPrimary(entry.parent_id)}
                                sx={{
                                  fontSize: 11,
                                  minWidth: "auto",
                                  px: 1.5,
                                  color: "#8b5cf6",
                                  textTransform: "none !important",
                                }}
                              >
                                Set Primary
                              </Button>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveParent(entry.parent_id)}
                            sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ))}

                  {/* Hint text */}
                  <Typography sx={{ fontSize: 11, color: "text.disabled", ml: 0.5 }}>
                    The primary parent/guardian will be used for monthly payment management and billing communications.
                  </Typography>
                </Box>
              )}

              {/* Empty state */}
              {parentEntries.length === 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 3,
                    px: 2,
                    bgcolor: "rgba(0,0,0,0.015)",
                    borderRadius: "8px",
                    border: "1px dashed rgba(0,0,0,0.1)",
                  }}
                >
                  <FamilyRestroomIcon sx={{ fontSize: 32, color: "rgba(0,0,0,0.15)", mb: 0.5 }} />
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    No parent/guardian added yet. Search above or create a new one.
                  </Typography>
                </Box>
              )}
            </SectionCard>

            {/* ─────────── SECTION 3: Academic Info ─────────── */}
            <SectionCard
              step={3}
              icon={<SchoolIcon />}
              title="Academic Information"
              subtitle="Study mode, enrollment details, and referral source"
              accentColor="#0ea5e9"
            >
              <Grid container spacing={2.5}>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Joined Month" />
                  <JoinedMonthPicker
                    value={formik.values.joined_month}
                    onChange={(d) => formik.setFieldValue("joined_month", d)}
                    error={Boolean(formik.errors.joined_month)}
                    helperText={formik.errors.joined_month as string}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Country of Origin" required />
                  <Autocomplete
                    options={countryOptions}
                    loading={countryIsLoading}
                    getOptionLabel={(o: CustomerOption) => o.label}
                    value={countryOptions.find((o) => o.value === Number(formik.values.student_country_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("student_country_id", v?.value || "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select country"
                        size="small"
                        error={Boolean(formik.errors.student_country_id)}
                        helperText={formik.errors.student_country_id as string}
                      />
                    )}
                  />
                </Grid>
                {/* Default Location: the student's preferred/primary campus. Used for
                    recommendation ordering, default filters and profile display — it
                    does NOT restrict the student from attending classes at other
                    locations. Class location always comes from the class itself. */}
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Default Location" required />
                  <Autocomplete
                    options={locationOptions}
                    loading={locationIsLoading}
                    getOptionLabel={(o: CustomerOption) => o.label}
                    value={locationOptions.find((o) => o.value === Number(formik.values.location_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("location_id", v?.value || "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select default location"
                        size="small"
                        error={Boolean(formik.touched.location_id && formik.errors.location_id)}
                        helperText={(formik.touched.location_id && (formik.errors.location_id as string)) || ""}
                        onBlur={() => formik.setFieldTouched("location_id", true)}
                      />
                    )}
                  />
                </Grid>

                {/* Heard From - Collapsible */}
                <Grid item xs={12}>
                  <Button
                    size="small"
                    endIcon={<ExpandMoreIcon sx={{ transform: showHeardFrom ? "rotate(180deg)" : "none", transition: "0.2s" }} />}
                    onClick={() => setShowHeardFrom(!showHeardFrom)}
                    sx={{ color: "text.secondary", fontSize: 12, textTransform: "none !important" }}
                  >
                    How did they hear about us?
                  </Button>
                  <Collapse in={showHeardFrom}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                      {Object.values(heardFrom).map((source) => (
                        <Chip
                          key={source}
                          label={source}
                          size="small"
                          onClick={() => formik.setFieldValue("heard_from", formik.values.heard_from === source ? "" : source)}
                          sx={{
                            cursor: "pointer",
                            fontWeight: 500,
                            ...(formik.values.heard_from === source
                              ? { bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` }
                              : { bgcolor: "rgba(0,0,0,0.04)", border: "1px solid transparent" }),
                          }}
                        />
                      ))}
                    </Box>
                  </Collapse>
                </Grid>
              </Grid>
            </SectionCard>

            {/* ─────────── SECTION 4: Location / Address ─────────── */}
            <SectionCard
              step={4}
              icon={<LocationOnIcon />}
              title="Address Details"
              subtitle="Search for an address to auto-fill, or enter manually"
              accentColor="#f59e0b"
            >
              {/* Address Search - Auto-fill */}
              <Box sx={{ mb: 3 }}>
                <FieldLabel label="Search Address" />
                <AddressAutocomplete
                  placeholder="Start typing an address to auto-fill..."
                  countryCode="ca"
                  onAddressSelect={(addr: ParsedAddress) => {
                    handleAddressAutoFill(addr);
                  }}
                />
                <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.5, ml: 0.5 }}>
                  Powered by OpenStreetMap. Select a result to auto-fill the fields below.
                </Typography>
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              {/* Manual address fields */}
              <Grid container spacing={2.5}>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Unit / Apt #" />
                  <TextField placeholder="e.g. Unit 204" size="small" fullWidth {...formik.getFieldProps("unit_number")} />
                </Grid>
                <Grid item lg={8} md={8} sm={6} xs={12}>
                  <FieldLabel label="Street" />
                  <TextField
                    placeholder="e.g. 123 Main St"
                    size="small"
                    fullWidth
                    {...formik.getFieldProps("street")}
                    InputProps={{
                      sx: {
                        ...(formik.values.street ? { bgcolor: alpha("#f59e0b", 0.04), borderColor: alpha("#f59e0b", 0.2) } : {}),
                      },
                    }}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="City / Town" />
                  <Autocomplete
                    freeSolo
                    options={cityOptions}
                    loading={cityIsLoading}
                    getOptionLabel={(o: any) => (typeof o === "string" ? o : o.label || "")}
                    value={cityOptions.find((o) => o.value === Number(formik.values.city_id)) || null}
                    onChange={(_, v) => {
                      if (typeof v === "string") {
                        // User typed a custom value and pressed Enter
                        handleCityFreeText(v);
                      } else {
                        formik.setFieldValue("city_id", v?.value || "");
                      }
                    }}
                    onBlur={(e: any) => {
                      const typed = e.target?.value?.trim();
                      if (!typed) return;
                      // If the user typed something but didn't select from dropdown
                      const alreadyMatched = cityOptions.find(
                        (o) => o.value === Number(formik.values.city_id) && o.label.toLowerCase() === typed.toLowerCase()
                      );
                      if (!alreadyMatched) {
                        handleCityFreeText(typed);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select or type city"
                        size="small"
                        helperText={formik.values.city_id ? undefined : "Type a city name if not in the list"}
                        FormHelperTextProps={{ sx: { fontSize: 11, color: "text.disabled" } }}
                      />
                    )}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Province / State" />
                  <Autocomplete
                    options={provinceOptions}
                    loading={provinceIsLoading}
                    getOptionLabel={(o: CustomerOption) => o.label}
                    value={provinceOptions.find((o) => o.value === Number(formik.values.province_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("province_id", v?.value || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Select province" size="small" />}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Postal Code" />
                  <TextField
                    placeholder="e.g. M1M 1M1"
                    size="small"
                    fullWidth
                    {...formik.getFieldProps("postal_code")}
                    InputProps={{
                      sx: {
                        ...(formik.values.postal_code ? { bgcolor: alpha("#f59e0b", 0.04) } : {}),
                      },
                    }}
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={6} xs={12}>
                  <FieldLabel label="Country" />
                  <Autocomplete
                    options={countryOptions}
                    loading={countryIsLoading}
                    getOptionLabel={(o: CustomerOption) => o.label}
                    value={countryOptions.find((o) => o.value === Number(formik.values.country_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("country_id", v?.value || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Select country" size="small" />}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ─────────── SECTION 5: Billing Information ─────────── */}
            <SectionCard
              step={5}
              icon={<ReceiptLongOutlinedIcon />}
              title="Billing Information"
              subtitle="Choose how this student will be billed. This setting applies to future invoices."
              accentColor="#0ea5e9"
            >
              <StudentBillingTypeSection
                mode="create"
                values={{
                  billing_type_v2: (formik.values.billing_type_v2 as any) || "",
                  monthly_amount: formik.values.monthly_amount ?? null,
                  billing_effective_from: formik.values.billing_effective_from ?? null,
                  tax_applicable: !!formik.values.tax_applicable,
                }}
                errors={{
                  billing_type_v2: formik.errors.billing_type_v2 as string | undefined,
                  monthly_amount: formik.errors.monthly_amount as string | undefined,
                  billing_effective_from: formik.errors
                    .billing_effective_from as string | undefined,
                }}
                touched={{
                  billing_type_v2: !!formik.touched.billing_type_v2,
                  monthly_amount: !!formik.touched.monthly_amount,
                  billing_effective_from: !!formik.touched
                    .billing_effective_from,
                }}
                onChange={(patch) => {
                  Object.entries(patch).forEach(([k, v]) =>
                    formik.setFieldValue(k, v as any)
                  );
                }}
                onBlur={(f) => formik.setFieldTouched(f as string, true)}
              />
            </SectionCard>

            {/* ─────────── SECTION 6: Notes ─────────── */}
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.08)",
                p: 3,
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              <FieldLabel label="Notes / Memo" />
              <TextField
                placeholder="Add any additional notes about the student..."
                size="small"
                fullWidth
                multiline
                rows={4}
                {...formik.getFieldProps("notes")}
              />
            </Box>

            {/* ─────────── Bottom Actions (Sticky) ─────────── */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                py: 2,
                position: "sticky",
                bottom: 0,
                bgcolor: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(8px)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                mx: -2,
                px: 2,
                mt: 1,
                zIndex: 10,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/student/list")}
                sx={{ color: "text.secondary", borderColor: "rgba(0,0,0,0.12)" }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={() => { formik.resetForm(); setSelectedImage(undefined); setParentEntries([]); }}
                sx={{ color: "text.secondary", borderColor: "rgba(0,0,0,0.12)" }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={() => formik.handleSubmit()}
                disabled={!formValid || isLoading}
                sx={{ minWidth: 140, boxShadow: "0 1px 3px rgba(0,0,0,0.12)" }}
              >
                {isLoading ? "Saving..." : "Save Student"}
              </Button>
            </Box>
          </Box>
          {isLoading && <BackDrop open={isLoading} />}
          {openCreateParent && (
            <NewDepartement
              handleCloseDialog={() => setOpenCreateParent(false)}
              openModel={openCreateParent}
              onSuccess={(newParent: any) => {
                const newEntry: ParentEntry = {
                  parent_id: newParent.id,
                  parentInfo: {
                    id: newParent.id,
                    first_name: newParent.first_name || "",
                    middle_name: newParent.middle_name || "",
                    last_name: newParent.last_name || "",
                    contact_number: newParent.contact_number || "",
                    email: newParent.email || "",
                    notes: "",
                    is_email_notification: true,
                    is_sms_notification: true,
                  },
                  relationship_type: "Parent",
                  isPrimary: parentEntries.length === 0,
                };
                const updated = [...parentEntries, newEntry];
                setParentEntries(updated);
                syncParentsToFormik(updated);
                setOpenCreateParent(false);
              }}
            />
          )}

          {/* Camera & Crop Modals */}
          <CameraModal
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCapture={handleCameraCapture}
          />
          <CropModal
            open={cropOpen}
            imageSrc={cropImageSrc}
            onClose={() => { setCropOpen(false); setCropImageSrc(null); }}
            onCropComplete={handleCropComplete}
          />
        </form>
      </FormikProvider>
    </Box>
  );
};

export default Index;
